import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import API from "../Network/API";
import "./Tutor/LiveRoom/LiveRoom.css";
import "./SessionConfirmation.css";

// Opened from the email sent after a 1-1 session: the student says whether it took place.
// Works signed out; answering needs a click because mail scanners open emailed links by themselves.
export default function SessionConfirmation() {
    const { t, i18n } = useTranslation();
    const { token } = useParams();
    const [data, setData] = useState(null);
    const [state, setState] = useState("loading"); // loading | ready | invalid | failed
    const [saying, setSaying] = useState(false);
    const [reason, setReason] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        API.get(`/v1/oauth/session-confirmation/${token}`)
            .then((response) => { setData(response.data.data); setState("ready"); })
            .catch((err) => setState(err?.response?.status === 404 ? "invalid" : "failed"));
    }, [token]);

    const answer = (value) => {
        if (value === "no" && reason.trim().length < 5) {
            setError(t("sessionConfirmation.reason_required"));
            return;
        }
        setBusy(true);
        setError("");
        API.post(`/v1/oauth/session-confirmation/${token}`, { answer: value, reason: value === "no" ? reason.trim() : undefined })
            .then((response) => setData(response.data.data))
            .catch((err) => setError(err?.response?.data?.message || t("sessionConfirmation.failed")))
            .finally(() => setBusy(false));
    };

    const when = data?.start_time
        ? new Date(data.start_time).toLocaleString(i18n.resolvedLanguage || i18n.language, { dateStyle: "medium", timeStyle: "short" })
        : "";

    if (state === "loading") {
        return <div className="lj-page container"><div className="lj-card"><span className="spinner-border lj-spinner" role="status" aria-hidden="true" /></div></div>;
    }
    if (state !== "ready") {
        return (
            <div className="lj-page container">
                <div className="lj-card">
                    <i className="fa-light fa-circle-exclamation lj-icon is-muted" />
                    <h4>{t(state === "invalid" ? "sessionConfirmation.invalid_title" : "sessionConfirmation.failed")}</h4>
                    {state === "invalid" && <p>{t("sessionConfirmation.invalid_text")}</p>}
                </div>
            </div>
        );
    }

    const closed = {
        confirmed: ["fa-circle-check", "thanks_title", "thanks_text"],
        disputed: ["fa-message-exclamation", "reported_title", "reported_text"],
        auto_released: ["fa-circle-info", "closed_title", "closed_text"],
        released: ["fa-circle-info", "closed_title", "closed_text"],
        refunded: ["fa-circle-info", "closed_title", "closed_text"],
        pending: ["fa-calendar-clock", "not_finished_title", "not_finished_text"],
    }[data.status] || ["fa-circle-info", "closed_title", "closed_text"];

    return (
        <div className="lj-page container">
            <div className="lj-card sc-card">
                {!data.can_answer ? (
                    <>
                        <i className={`fa-light ${closed[0]} lj-icon`} />
                        <h4>{t(`sessionConfirmation.${closed[1]}`)}</h4>
                        <p>{t(`sessionConfirmation.${closed[2]}`)}</p>
                    </>
                ) : (
                    <>
                        <i className="fa-light fa-chalkboard-user lj-icon" />
                        <h4>{t("sessionConfirmation.title")}</h4>
                        <dl className="sc-details">
                            {data.course && <><dt>{t("sessionConfirmation.course")}</dt><dd>{data.course}</dd></>}
                            {data.lesson && <><dt>{t("sessionConfirmation.lesson")}</dt><dd>{data.lesson}</dd></>}
                            {data.tutor && <><dt>{t("sessionConfirmation.tutor")}</dt><dd>{data.tutor}</dd></>}
                            {when && <><dt>{t("sessionConfirmation.time")}</dt><dd>{when}</dd></>}
                        </dl>
                        {saying ? (
                            <div className="sc-form">
                                <label htmlFor="sc-reason">{t("sessionConfirmation.reason_label")}</label>
                                <textarea id="sc-reason" className="form-control" rows={4} maxLength={2000} value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t("sessionConfirmation.reason_placeholder")} />
                                {error && <div className="sc-error">{error}</div>}
                                <div className="lj-actions">
                                    <button type="button" className="btn btn-outline-secondary" disabled={busy} onClick={() => { setSaying(false); setError(""); }}>{t("sessionConfirmation.back")}</button>
                                    <button type="button" className="btn lr-btn-primary" disabled={busy} onClick={() => answer("no")}>{t("sessionConfirmation.send")}</button>
                                </div>
                            </div>
                        ) : (
                            <>
                                {error && <div className="sc-error">{error}</div>}
                                <div className="lj-actions">
                                    <button type="button" className="btn lr-btn-primary" disabled={busy} onClick={() => answer("yes")}>{t("sessionConfirmation.yes")}</button>
                                    <button type="button" className="btn btn-outline-secondary" disabled={busy} onClick={() => setSaying(true)}>{t("sessionConfirmation.no")}</button>
                                </div>
                                <p className="sc-note">{t("sessionConfirmation.note")}</p>
                            </>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
